import React, { useCallback, useEffect, useMemo, useRef, useState, createElement } from 'react';
import './index.scss';
import { Form as NextForm, ResponsiveGrid, Input, Select, Radio, Checkbox, DatePicker, NumberPicker, Upload, Button, } from '@alifd/next';
const DEBUG_PREFIX = '[CustomForm v0.4]';
const cfLog = (tag, ...args) => {
    if (typeof window !== 'undefined' && window.__CUSTOM_FORM_DEBUG__ === false)
        return;
    console.log(`${DEBUG_PREFIX} [${tag}]`, ...args);
};
const cfWarn = (tag, ...args) => {
    console.warn(`${DEBUG_PREFIX} [${tag}]`, ...args);
};
const NextFormAny = NextForm;
const ResponsiveGridAny = ResponsiveGrid;
const ResponsiveGridCell = ResponsiveGrid.Cell;
const FormItemAny = NextForm.Item;
const InputAny = Input;
const TextAreaAny = Input.TextArea;
const SelectAny = Select;
const ButtonAny = Button;
const RadioGroupAny = Radio.Group;
const CheckboxGroupAny = Checkbox.Group;
const DatePickerAny = DatePicker;
const NumberPickerAny = NumberPicker;
const UploadAny = Upload;
const renderTemplate = (template, values) => {
    return template.replace(/\{(\w+)\}/g, (_, key) => {
        const val = values[key];
        return val !== undefined && val !== null ? String(val) : '';
    });
};
const normalizeInitialValues = (raw) => {
    if (!raw)
        return {};
    if (Array.isArray(raw)) {
        return raw.reduce((acc, item) => {
            if (item && item.field) {
                acc[item.field] = item.value !== undefined ? item.value : '';
            }
            return acc;
        }, {});
    }
    return raw;
};
const normalizeComputedFields = (raw) => {
    if (!raw)
        return {};
    if (Array.isArray(raw)) {
        return raw.reduce((acc, item) => {
            if (item && item.targetField) {
                acc[item.targetField] = item.template || '';
            }
            return acc;
        }, {});
    }
    return raw;
};
const applyComputedFields = (values, computedFields) => {
    if (!computedFields || Object.keys(computedFields).length === 0)
        return values;
    const result = { ...values };
    Object.keys(computedFields).forEach((key) => {
        result[key] = renderTemplate(computedFields[key], result);
    });
    cfLog('computedFields', '计算字段结果', result);
    return result;
};
const runSubmitDataSource = (submitDataSource, values, errors, field) => {
    if (!submitDataSource)
        return;
    if (typeof submitDataSource === 'function') {
        submitDataSource(values, errors, field);
        return;
    }
    if (typeof submitDataSource?.load === 'function') {
        submitDataSource.load(values);
        return;
    }
    if (typeof submitDataSource?.run === 'function') {
        submitDataSource.run(values, errors, field);
        return;
    }
    if (typeof submitDataSource?.execute === 'function') {
        submitDataSource.execute(values, errors, field);
        return;
    }
    if (typeof submitDataSource?.request === 'function') {
        submitDataSource.request(values, errors, field);
        return;
    }
    if (typeof submitDataSource?.submit === 'function') {
        submitDataSource.submit(values, errors, field);
    }
};
const renderFormItemComponent = (item) => {
    const componentProps = item.componentProps || {};
    const rawDataSource = item.optionsBind?.length
        ? item.optionsBind
        : (componentProps.dataSource || item.options || []);
    const labelKey = item.optionsLabelKey;
    const valueKey = item.optionsValueKey;
    const dataSource = (labelKey || valueKey) && Array.isArray(rawDataSource)
        ? rawDataSource.map((opt) => ({
            ...opt,
            ...(labelKey ? { label: opt[labelKey] ?? opt.label } : {}),
            ...(valueKey ? { value: opt[valueKey] ?? opt.value } : {}),
        }))
        : rawDataSource;
    switch (item.componentType) {
        case 'TextArea':
            return createElement(TextAreaAny, { ...componentProps });
        case 'Select':
            return createElement(SelectAny, { ...componentProps, dataSource: dataSource });
        case 'RadioGroup':
            return createElement(RadioGroupAny, { ...componentProps, dataSource: dataSource });
        case 'CheckboxGroup':
            return createElement(CheckboxGroupAny, { ...componentProps, dataSource: dataSource });
        case 'NumberPicker':
            return createElement(NumberPickerAny, { ...componentProps });
        case 'DatePicker':
            return createElement(DatePickerAny, { format: "YYYY-MM-DD", ...componentProps });
        case 'DateTimePicker': {
            const { format: cpFormat, showTime: _ignoredShowTime, ...restCp } = componentProps;
            const dtFmt = cpFormat || 'YYYY-MM-DD HH:mm:ss';
            const spaceIdx = dtFmt.indexOf(' ');
            const dateFmt = spaceIdx !== -1 ? dtFmt.slice(0, spaceIdx) : dtFmt;
            const timeFmt = spaceIdx !== -1 ? dtFmt.slice(spaceIdx + 1) : 'HH:mm:ss';
            return (createElement(DatePickerAny, { ...restCp, format: dateFmt, showTime: { format: timeFmt } }));
        }
        case 'Upload':
            return createElement(UploadAny, { ...componentProps });
        case 'Input':
        default:
            return createElement(InputAny, { ...componentProps });
    }
};
const CustomForm = ({ columns = 1, spacing = [0, 16], emptyContent = '添加表单项', formItems = [], initialValues, computedFields, fieldLinkage, submitMapping, fullWidth: globalFullWidth, showSubmit = true, submitText = '提交', submitValidate = true, submitDataSource, submitButtonProps, showReset = false, resetText = '重置', resetButtonProps, operations, operationAlign = 'center', onSubmit, onSubmitFailed, onChange, onReset, children, labelAlign, labelCol, size: globalSize, ...otherProps }) => {
    const fixedColumns = columns;
    const normalizedGap = useMemo(() => {
        if (!Array.isArray(spacing))
            return spacing;
        if (spacing.length >= 2)
            return [spacing[0], spacing[1]];
        return [spacing[0] ?? 0, spacing[0] ?? 16];
    }, [spacing]);
    const resolvedInitialValues = useMemo(() => normalizeInitialValues(initialValues), [initialValues]);
    const resolvedComputedFields = useMemo(() => normalizeComputedFields(computedFields), [computedFields]);
    const valuesRef = useRef(resolvedInitialValues ? { ...resolvedInitialValues } : {});
    const formItemsRef = useRef(formItems);
    useEffect(() => { formItemsRef.current = formItems; }, [formItems]);
    const [linkageValues, setLinkageValues] = useState({});
    const lastInitSerialRef = useRef('');
    useEffect(() => {
        const serial = JSON.stringify(resolvedInitialValues);
        if (serial !== lastInitSerialRef.current) {
            lastInitSerialRef.current = serial;
            valuesRef.current = resolvedInitialValues ? { ...resolvedInitialValues } : {};
            setLinkageValues({});
            cfLog('initialValues', 'initialValues 已变更，重新初始化 valuesRef', resolvedInitialValues);
        }
    }, [resolvedInitialValues]);
    const handleFieldChange = useCallback((field, value) => {
        valuesRef.current = { ...valuesRef.current, [field]: value };
        cfLog('onChange', `字段 "${field}" 变化`, { value, allValues: valuesRef.current });
        if (fieldLinkage && fieldLinkage.length > 0) {
            const clearableFields = new Set();
            fieldLinkage.forEach((rule) => {
                if (rule.watchField !== field)
                    return;
                if (rule.fillField)
                    clearableFields.add(rule.fillField);
                if (rule.fillFields)
                    Object.keys(rule.fillFields).forEach((f) => clearableFields.add(f));
                if (rule.staticRules) {
                    Object.values(rule.staticRules).forEach((ruleMap) => Object.keys(ruleMap).forEach((f) => clearableFields.add(f)));
                }
            });
            const fills = {};
            fieldLinkage.forEach((rule) => {
                if (rule.watchField !== field)
                    return;
                if (rule.staticRules) {
                    const staticFill = rule.staticRules[String(value)];
                    if (staticFill) {
                        Object.assign(fills, staticFill);
                        cfLog('linkage', `静态联动 "${field}" = ${value}，回填`, staticFill);
                    }
                }
                if (rule.dataSource && rule.fillFields) {
                    if (!Array.isArray(rule.dataSource)) {
                        cfWarn('linkage', `动态联动（watchField="${rule.watchField}"）的 dataSource 不是数组` +
                            `（实际类型：${typeof rule.dataSource}，值：${JSON.stringify(rule.dataSource)}）。` +
                            `请在属性面板中通过「变量绑定」控件选择数据源变量，不要直接输入文字路径。`);
                    }
                    else {
                        const matchKey = rule.matchKey
                            || formItemsRef.current.find((i) => i.field === rule.watchField)?.optionsValueKey
                            || 'value';
                        const matched = rule.dataSource.find((item) => item[matchKey] === value ||
                            (item[matchKey] !== undefined &&
                                value !== undefined &&
                                String(item[matchKey]) === String(value)));
                        if (matched) {
                            const fill = {};
                            Object.entries(rule.fillFields).forEach(([formField, dsKey]) => {
                                if (formField === field) {
                                    cfWarn('linkage', `fillFields 中的 key "${formField}" 与监听字段 watchField 相同，已跳过。` +
                                        `fillFields 格式为 {"目标表单字段":"数据源字段名"}，请检查 key/value 是否写反。`);
                                    return;
                                }
                                if (!(dsKey in matched)) {
                                    cfWarn('linkage', `fillFields 中数据源字段名 "${dsKey}" 在匹配项中不存在，已跳过。` +
                                        `匹配项可用字段：${Object.keys(matched).join(', ')}`);
                                    return;
                                }
                                fill[formField] = matched[dsKey];
                            });
                            Object.assign(fills, fill);
                            cfLog('linkage', `动态联动 "${field}" = ${value}，回填`, fill);
                        }
                        else {
                            cfWarn('linkage', `动态联动：在 dataSource 中未找到 matchKey="${matchKey}" 等于 "${value}" 的记录。` +
                                `请确认 matchKey 与 optionsBind 存储值字段名一致（配置了 optionsValueKey 时会自动使用该字段名），且 dataSource 与 optionsBind 同源。`);
                        }
                    }
                }
                if (rule.fillField !== undefined && rule.fillValue !== undefined) {
                    const noConstraint = rule.matchValue === undefined || rule.matchValue === '';
                    const matched = noConstraint || String(value) === String(rule.matchValue);
                    if (matched) {
                        fills[rule.fillField] = rule.fillValue;
                        cfLog('linkage', `简单联动 "${field}" = ${value}，回填`, { [rule.fillField]: rule.fillValue });
                    }
                }
            });
            if (clearableFields.size > 0) {
                clearableFields.forEach((f) => {
                    const initVal = resolvedInitialValues?.[f];
                    if (initVal !== undefined) {
                        valuesRef.current[f] = initVal;
                    }
                    else {
                        delete valuesRef.current[f];
                    }
                });
                Object.assign(valuesRef.current, fills);
                setLinkageValues((prev) => {
                    const next = { ...prev };
                    clearableFields.forEach((f) => {
                        if (!(f in fills))
                            delete next[f];
                    });
                    return { ...next, ...fills };
                });
            }
            else if (Object.keys(fills).length > 0) {
                Object.assign(valuesRef.current, fills);
                setLinkageValues((prev) => ({ ...prev, ...fills }));
            }
        }
        const allValues = applyComputedFields(valuesRef.current, resolvedComputedFields);
        if (onChange) {
            onChange(field, value, allValues);
        }
    }, [resolvedComputedFields, resolvedInitialValues, fieldLinkage, onChange]);
    const alignJustifyMap = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end',
    };
    const effectiveOperations = useMemo(() => {
        if (operations !== undefined)
            return operations;
        const ops = [];
        if (showReset)
            ops.push({ text: resetText || '重置', action: 'reset', type: 'normal' });
        if (showSubmit)
            ops.push({ text: submitText || '提交', action: 'submit', type: 'primary', submitValidate });
        return ops;
    }, [operations, showSubmit, submitText, submitValidate, showReset, resetText]);
    const handleSubmit = useCallback((values, errors, field, buttonDataSource) => {
        const mergedValues = { ...values, ...valuesRef.current };
        let finalValues = applyComputedFields(mergedValues, resolvedComputedFields);
        if (submitMapping) {
            const mode = submitMapping.mode ?? (submitMapping.items?.length ? 'itemList' : 'passthrough');
            if (mode === 'rename' && submitMapping.fieldRenameMap?.length) {
                const renamed = { ...finalValues };
                submitMapping.fieldRenameMap.forEach(({ fromField, toField }) => {
                    if (fromField && toField && fromField in renamed) {
                        renamed[toField] = renamed[fromField];
                        if (fromField !== toField)
                            delete renamed[fromField];
                    }
                });
                finalValues = renamed;
                cfLog('submitMapping', 'rename 模式字段重命名完成', finalValues);
            }
            else if (mode === 'itemList' && submitMapping.items?.length) {
                const targetField = submitMapping.targetField || 'itemList';
                const itemList = submitMapping.items.map((mappingItem) => {
                    let columnValue;
                    if (mappingItem.field) {
                        columnValue = finalValues[mappingItem.field] ?? '';
                    }
                    else if (mappingItem.template) {
                        columnValue = renderTemplate(mappingItem.template, finalValues);
                    }
                    else {
                        columnValue = '';
                    }
                    return { columnName: mappingItem.columnName, columnValue };
                });
                finalValues = { ...finalValues, [targetField]: itemList };
                cfLog('submitMapping', `itemList 模式已生成 ${targetField}`, itemList);
            }
        }
        cfLog('submit', '提交触发', { rawValues: values, errors, finalValues });
        if (errors && Object.keys(errors).length) {
            cfWarn('submit', '校验失败', errors);
            if (onSubmitFailed) {
                onSubmitFailed(finalValues, errors, field);
            }
            return;
        }
        cfLog('submit', '校验通过，准备提交', finalValues);
        if (onSubmit) {
            onSubmit(finalValues, errors, field);
        }
        const effectiveDS = buttonDataSource ?? submitDataSource;
        runSubmitDataSource(effectiveDS, finalValues, errors, field);
    }, [resolvedComputedFields, submitMapping, onSubmit, onSubmitFailed, submitDataSource]);
    const handleReset = useCallback(() => {
        setLinkageValues({});
        valuesRef.current = resolvedInitialValues ? { ...resolvedInitialValues } : {};
        cfLog('reset', '表单已重置，linkageValues 和 valuesRef 已恢复初始状态');
        onReset?.();
    }, [resolvedInitialValues, onReset]);
    const formItemNodes = formItems.map((item, index) => {
        const colSpan = Math.min(columns, Math.max(1, item.columnSpan || 1));
        const fieldInitialValue = item.initialValue !== undefined
            ? item.initialValue
            : (linkageValues[item.field] ?? resolvedInitialValues?.[item.field]);
        const effectiveFullWidth = item.fullWidth !== undefined ? item.fullWidth : globalFullWidth;
        const effectiveSize = item.size || globalSize;
        const componentPropsWithChange = {
            ...(item.placeholder ? { placeholder: item.placeholder } : {}),
            ...(effectiveSize ? { size: effectiveSize } : {}),
            ...(effectiveFullWidth !== undefined
                ? { style: { width: effectiveFullWidth ? '100%' : undefined, ...(item.componentProps?.style || {}) } }
                : {}),
            ...(item.componentProps || {}),
            onChange: (value) => {
                if (typeof item.componentProps?.onChange === 'function') {
                    item.componentProps.onChange(value);
                }
                let storedValue = value;
                if (['DatePicker', 'DateTimePicker'].includes(item.componentType) && value && typeof value.format === 'function') {
                    const fmt = item.componentProps?.format || (item.componentType === 'DateTimePicker' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD');
                    storedValue = value.format(fmt);
                }
                handleFieldChange(item.field, storedValue);
            },
        };
        const isLinkageControlled = linkageValues[item.field] !== undefined && item.initialValue === undefined;
        if (isLinkageControlled) {
            componentPropsWithChange.value = linkageValues[item.field];
        }
        else if (fieldInitialValue !== undefined && componentPropsWithChange.value === undefined) {
            componentPropsWithChange.defaultValue = fieldInitialValue;
            cfLog('renderItem', `字段 "${item.field}" 注入初始值`, fieldInitialValue);
        }
        if (!item.field) {
            cfWarn('renderItem', `第 ${index + 1} 个表单项缺少 field 字段，onChange 将无法绑定`);
        }
        const mergedFormItemProps = {
            ...(labelAlign ? { labelAlign } : {}),
            ...(labelCol ? { labelCol } : {}),
            ...(effectiveSize ? { size: effectiveSize } : {}),
            ...(item.help ? { help: item.help } : {}),
            ...(item.extra ? { extra: item.extra } : {}),
            ...(item.formItemProps || {}),
        };
        return (createElement(ResponsiveGridCell, { colSpan: colSpan, key: `${item.field || index}-form-item` },
            createElement(FormItemAny, { label: item.label, name: item.field, required: item.required, ...mergedFormItemProps }, renderFormItemComponent({ ...item, componentProps: componentPropsWithChange }))));
    });
    const childrenNodes = React.Children.toArray(children).map((child, index) => {
        if (React.isValidElement(child) && child.type === ResponsiveGridCell) {
            return child;
        }
        return (createElement(ResponsiveGridCell, { colSpan: 1, key: `custom-form-child-${index}` }, child));
    });
    const hasContent = formItemNodes.length > 0 || childrenNodes.length > 0;
    const formInitialProps = {};
    if (resolvedInitialValues && Object.keys(resolvedInitialValues).length > 0) {
        formInitialProps.defaultValue = resolvedInitialValues;
        cfLog('render', '表单携带 initialValues 渲染', resolvedInitialValues);
    }
    return (createElement(NextFormAny, { className: "custom-form", ...(labelAlign ? { labelAlign } : {}), ...(labelCol ? { labelCol } : {}), ...(globalSize ? { size: globalSize } : {}), ...formInitialProps, ...otherProps },
        hasContent ? (createElement(ResponsiveGridAny, { className: "custom-form__grid", device: "desktop", gap: normalizedGap, columns: fixedColumns },
            formItemNodes,
            childrenNodes)) : (createElement("div", { className: "custom-form__empty" }, emptyContent)),
        effectiveOperations.length > 0 ? (createElement("div", { className: "custom-form__actions", style: { justifyContent: alignJustifyMap[operationAlign] || 'center' } }, effectiveOperations.map((op, opIndex) => {
            if (op.action === 'reset') {
                return (createElement(NextFormAny.Reset, { key: opIndex, type: op.type || 'normal', onClick: handleReset, ...(op.buttonProps || {}) }, op.text));
            }
            if (op.action === 'submit') {
                const btnValidate = op.submitValidate !== undefined ? op.submitValidate : submitValidate;
                return (createElement(NextFormAny.Submit, { key: opIndex, type: op.type || 'primary', validate: btnValidate, onClick: (values, errors, field) => handleSubmit(values, errors, field, op.dataSource), ...(op.buttonProps || {}) }, op.text));
            }
            return (createElement(ButtonAny, { key: opIndex, type: op.type || 'normal', onClick: op.onClick, ...(op.buttonProps || {}) }, op.text));
        }))) : null));
};
CustomForm.displayName = 'CustomForm';
export default CustomForm;
